import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { ArrowLeft, Search, Settings, Layers, BookOpen, FileText } from 'lucide-react';
import RyanFieldPattern from './RyanFieldPattern';
import { CYCLE_ICONS } from '../../components/dashboard/ExploreContent';
import { getRyanFieldColor } from '../constants/ryanFieldVisuals';
import { RYAN_MEDICAL_ICON_MAP } from '../constants/ryanMedicalIcons';
import './RyanFieldHero.css';

interface RyanFieldHeroProps {
  name: string;
  colorId: string;
  theme?: string;
  icon: string;
  decksCount: number;
  flashcardsTotal: number;
  apostilasCount: number;
  onBack: () => void;
  searchQuery: string;
  onSearchChange: (value: string) => void;
  isAdmin: boolean;
  adminMenuContent?: React.ReactNode;
}

const RyanFieldHero: React.FC<RyanFieldHeroProps> = ({
  name,
  colorId,
  theme,
  icon,
  decksCount,
  flashcardsTotal,
  apostilasCount,
  onBack,
  searchQuery,
  onSearchChange,
  isAdmin,
  adminMenuContent,
}) => {
  const color = getRyanFieldColor(colorId);
  const IconComp = (RYAN_MEDICAL_ICON_MAP[icon] || CYCLE_ICONS[icon]) as React.ComponentType<any> || Layers;
  // Gradiente diagonal sutil (mesmo espírito do .sdp-hero-header do GRAIA:
  // tom mais escuro → cor base → tom mais claro), derivado da cor do campo
  // via color-mix() em vez de valores fixos, pra funcionar com qualquer cor da paleta.
  const heroBackground = `linear-gradient(135deg, color-mix(in oklch, ${color} 88%, black) 0%, ${color} 55%, color-mix(in oklch, ${color} 85%, white) 100%)`;

  // Menu admin renderizado via portal em document.body — o header usa
  // overflow:hidden pra recortar a ilustração de fundo, o que também cortaria
  // o dropdown se ele ficasse dentro da árvore normal (mesmo padrão de portal
  // já usado no kebab de RyanHomePage/RyanSubdeckCard).
  const [showAdminMenu, setShowAdminMenu] = useState(false);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });
  const gearBtnRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const toggleAdminMenu = () => {
    if (!showAdminMenu && gearBtnRef.current) {
      const rect = gearBtnRef.current.getBoundingClientRect();
      setMenuPos({
        top: rect.bottom + window.scrollY + 6,
        left: rect.right + window.scrollX - 220,
      });
    }
    setShowAdminMenu(v => !v);
  };

  useEffect(() => {
    if (!showAdminMenu) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (gearBtnRef.current?.contains(target) || dropdownRef.current?.contains(target)) return;
      setShowAdminMenu(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showAdminMenu]);

  return (
    <div className="ryan-field-hero" style={{ background: heroBackground }}>
      <div className="ryan-field-hero-pattern">
        <RyanFieldPattern theme={theme} size="header" />
      </div>

      <div className="ryan-field-hero-inner">
        <div className="ryan-field-hero-top">
          <button className="ryan-field-hero-back-btn" onClick={onBack} title="Campos de Estudo">
            <ArrowLeft size={16} />
          </button>

          <div className="ryan-field-hero-center">
            <div className="ryan-field-hero-center-row">
              <div className="ryan-field-hero-icon">
                <IconComp size={24} />
              </div>
              <h1>{name}</h1>
            </div>
            <div className="ryan-field-hero-sub">
              {[
                decksCount > 0 && (
                  <span key="decks" className="ryan-field-hero-sub-item">
                    <Layers size={13} /> {decksCount} deck{decksCount !== 1 ? 's' : ''}
                  </span>
                ),
                flashcardsTotal > 0 && (
                  <span key="flashcards" className="ryan-field-hero-sub-item">
                    <BookOpen size={13} /> {flashcardsTotal} flashcard{flashcardsTotal !== 1 ? 's' : ''}
                  </span>
                ),
                apostilasCount > 0 && (
                  <span key="apostilas" className="ryan-field-hero-sub-item">
                    <FileText size={13} /> {apostilasCount} apostila{apostilasCount !== 1 ? 's' : ''}
                  </span>
                ),
              ]
                .filter(Boolean)
                .map((item, i, arr) => (
                  <React.Fragment key={i}>
                    {item}
                    {i < arr.length - 1 && <span className="ryan-field-hero-dot">·</span>}
                  </React.Fragment>
                ))}
            </div>
          </div>
        </div>

        <div className="ryan-field-hero-search-row">
          <div className="ryan-field-hero-search">
            <Search size={15} />
            <input
              type="text"
              value={searchQuery}
              onChange={e => onSearchChange(e.target.value)}
              placeholder="Buscar decks e apostilas neste campo…"
            />
          </div>

          {isAdmin && (
            <button
              ref={gearBtnRef}
              className="ryan-field-hero-gear-btn"
              onClick={toggleAdminMenu}
              title="Ações admin"
            >
              <Settings size={16} />
            </button>
          )}
        </div>
      </div>

      {isAdmin && showAdminMenu && ReactDOM.createPortal(
        <div
          ref={dropdownRef}
          className="ryan-field-hero-admin-dropdown"
          style={{ top: menuPos.top, left: menuPos.left }}
          onClick={() => setShowAdminMenu(false)}
        >
          {adminMenuContent}
        </div>,
        document.body
      )}
    </div>
  );
};

export default RyanFieldHero;
